// Salesforce integration types (mirrors backend DTOs — see GetSalesforceAuthStatus.cs)

import type { IntegrationErrorEntryDto } from '@/types/integrations'

// Auth status — returned from anonymous objects (camelCase). recentErrors entries serialize
// as PascalCase (same as the standard integration list), matching IntegrationErrorEntryDto.
export interface SalesforceConnectionStatus {
    isConnected: boolean
    // IntegrationConnection id — surfaced so the panel can drive the shared on-demand
    // AI-summary fetch and reuse the standard error-detail flow.
    id?: string
    status?: string
    scopes?: string
    // Per-org REST host. Salesforce access tokens carry no expiry, so there is no expiry field.
    instanceUrl?: string | null
    isArchived?: boolean
    // Health parity — projected from the IntegrationConnection entity by the status DTO.
    isSoftFailure?: boolean
    consecutiveFailureCount?: number
    recentErrors?: IntegrationErrorEntryDto[]
    aiSummary?: string | null
    // Summary readout fields.
    connectedAtUtc?: string | null
    lastOutboundSyncUtc?: string | null
    lastInboundSyncUtc?: string | null
}

export interface SalesforceAuthInitiateResult {
    authorizationUrl: string
}

// ---------------------------------------------------------------------------
// Campaign configuration (ORB-659) — mirrors SalesforceMappingConfig.cs. PascalCase on purpose: the API serializes its DTOs
// PascalCase, and the HubSpot config types alongside these follow the same convention.
// ---------------------------------------------------------------------------

/**
 * The canonical engagement states OrbitalX owns. These are wire values, never shown to a user —
 * each one is mapped to whatever the customer calls it on their own CampaignMemberStatus.
 */
export const ENGAGEMENT_STATES = ['registered', 'attended', 'did_not_attend', 'responded'] as const
export type EngagementState = (typeof ENGAGEMENT_STATES)[number]

/** How each state reads in the configurator. The customer never sees the wire value. */
export const ENGAGEMENT_STATE_LABELS: Record<EngagementState, string> = {
    registered: 'Registered for an online event',
    attended: 'Attended an online event',
    did_not_attend: 'Registered but did not attend',
    responded: 'Downloaded an asset, requested a demo, or submitted a lead magnet',
}

/** OrbitalX web types, as the API names them on CrmContactSyncRequest.EventType. */
export const WEB_EVENT_TYPES = ['webinar', 'downloadable_asset', 'demo_request', 'lead_magnet'] as const
export type WebEventType = (typeof WEB_EVENT_TYPES)[number]

export const WEB_EVENT_TYPE_LABELS: Record<WebEventType, string> = {
    webinar: 'Online event',
    downloadable_asset: 'Downloadable asset',
    demo_request: 'Demo request',
    lead_magnet: 'Lead magnet',
}

export interface SalesforceCampaignStatusMapping {
    EngagementState: EngagementState
    /** The customer's CampaignMemberStatus label. Created on the campaign if it does not exist. */
    StatusLabel: string
    /** Whether Salesforce counts this status as a response. Null means use our default. */
    HasResponded?: boolean | null
}

export interface SalesforceCampaignTypeMapping {
    EventType: WebEventType
    /** The customer's Campaign.Type picklist value. */
    CampaignType: string
}

export interface SalesforceCampaignConfig {
    /** Prepended to the campaign name. Cosmetic — campaigns are matched on a hidden id. */
    NamePrefix?: string | null
    /** Written to Campaign.ParentId. Lets the customer filter everything we create in one go. */
    ParentCampaignId?: string | null
    /** Written to Campaign.OwnerId. Defaults to whoever authorized the connection. */
    OwnerId?: string | null
    MemberStatuses: SalesforceCampaignStatusMapping[]
    CampaignTypes: SalesforceCampaignTypeMapping[]
}

export interface SalesforceMappingConfig {
    Campaign: SalesforceCampaignConfig
    /** Null until the configuration has been saved at least once. */
    ConfiguredAtUtc?: string | null
}
